import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import { operationsApi } from '../api/services/operationsApi';
import { BRANDFETCH_CLIENT_ID } from '../constants/brand';
import {
  DEFAULT_HISTORY_FILTERS,
  TRANSACTION_TYPE_FILTERS,
} from '../constants/historyData';
import { getMccCategoryKey } from '../constants/mccCategories';
import {
  formatDecimal,
  parseDecimalInput,
  scaledIntToNormal,
} from '../utils/decimalValues';
import {
  DEFAULT_LANGUAGE,
  normalizeLanguage,
} from '../translation';
import { getCurrencyIconUrl } from '../utils/currencyIcon';
import { isUnlockSessionReady } from './unlockSession';

const TRANSACTIONS_UPDATED_EVENT = 'sandrapay:transactions-updated';
const LOCALIZED_TRANSACTION_TEXT = {
  cardIssuancePayment: '__transaction.cardIssuancePayment',
  cashWithdrawal: '__transaction.cashWithdrawal',
  depositAccount: '__transaction.depositAccount',
  paymentCanceled: '__transaction.paymentCanceled',
  refund: '__transaction.refund',
};
const DATE_RANGE_ORDER = ['today', 'yesterday', 'older'];
const KNOWN_DATE_GROUP_ORDER = {
  today: 0,
  yesterday: 1,
};
const TOP_UP_TYPE_MARKERS = ['deposit', 'top_up', 'topup', 'income', 'refund'];
const CARD_CREATE_OPERATION_TYPES = new Set(['card_create', 'create_card']);
const PAYMENT_CANCELED_OPERATION_TYPES = new Set(['payment_canceled']);
const PAYMENT_WITHDRAWAL_OPERATION_TYPES = new Set(['payment_withdrawal']);
const USD_AMOUNT_DECIMALS = 2;
const DEFAULT_FIAT_AMOUNT_DECIMALS = 2;
let transactionsState = {
  items: [],
  isLoaded: false,
  isLoading: false,
  isMore: false,
  error: null,
  lastId: null,
};

dayjs.extend(utc);

function emitTransactionsUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TRANSACTIONS_UPDATED_EVENT));
  }
}

function getLocale(language) {
  return normalizeLanguage(language) || DEFAULT_LANGUAGE;
}

function getResponseData(response) {
  return Array.isArray(response)
    ? response
    : Array.isArray(response?.data)
      ? response.data
      : [];
}

function getOperationId(operation) {
  return operation?._id || operation?.id || operation?.operation_id || '';
}

function getEnrichedObject(operation) {
  return operation?.enriched_data?.data || operation?.enrichedData?.data || {};
}

function getEnrichedMerchant(operation) {
  return getEnrichedObject(operation)?.merchant || {};
}

function getEnrichedCategory(operation) {
  return getEnrichedObject(operation)?.category || {};
}

function sanitizeDomain(domain) {
  const raw = String(domain || '').trim();

  if (!raw) {
    return '';
  }

  return raw
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split('/')[0]
    .split('?')[0]
    .toLowerCase();
}

function buildBrandfetchIconUrl(domain) {
  const normalizedDomain = sanitizeDomain(domain);

  if (!normalizedDomain) {
    return '';
  }

  return `https://cdn.brandfetch.io/domain/${encodeURIComponent(normalizedDomain)}/icon.svg/fallback/404?c=${BRANDFETCH_CLIENT_ID}`;
}

function getInitialLetter(value) {
  const normalized = String(value || '').trim();

  return normalized ? normalized[0].toUpperCase() : '?';
}

function getCategoryKey(operation) {
  const category = getEnrichedCategory(operation);
  const mcc = category?.code?.mcc ?? category?.mcc;

  if (isCardCreateOperation(operation)) {
    return 'cardIssuance';
  }

  if (isPaymentWithdrawalOperation(operation)) {
    return 'cashWithdrawal';
  }

  if (mcc !== undefined && mcc !== null) {
    return getMccCategoryKey(mcc);
  }

  if (isIncomeOperation(operation)) {
    return 'topUp';
  }

  return 'general';
}

function isCardCreateOperation(operation) {
  return CARD_CREATE_OPERATION_TYPES.has(
    String(operation?.type || '').trim().toLowerCase(),
  );
}

function isPaymentCanceledOperation(operation) {
  return PAYMENT_CANCELED_OPERATION_TYPES.has(
    String(operation?.type || '').trim().toLowerCase(),
  );
}

function isPaymentWithdrawalOperation(operation) {
  return PAYMENT_WITHDRAWAL_OPERATION_TYPES.has(
    String(operation?.type || '').trim().toLowerCase(),
  );
}

function isIncomeOperation(operation) {
  const type = String(operation?.type || '').toLowerCase();

  return TOP_UP_TYPE_MARKERS.some((marker) => type.includes(marker));
}

function getOperationDateValue(operation) {
  const utcValue = operation?.original_utc_datetime || operation?.utc_datetime;

  if (utcValue) {
    return normalizeUtcDateValue(utcValue);
  }

  return operation?.created_at
    || operation?.createdAt
    || operation?.date
    || null;
}

function normalizeUtcDateValue(value) {
  const raw = String(value || '').trim();

  if (!raw) {
    return null;
  }

  const trimmedMicroseconds = raw.replace(/\.(\d{3})\d+/, '.$1');

  return /(?:z|[+-]\d{2}:?\d{2})$/i.test(trimmedMicroseconds)
    ? trimmedMicroseconds
    : `${trimmedMicroseconds}Z`;
}

function normalizeScaledAmount(value, decimalPlaces = DEFAULT_FIAT_AMOUNT_DECIMALS) {
  const raw = String(value ?? '').trim();

  if (!raw) {
    return '0';
  }

  if (!/^-?\d+(?:[.,]\d+)?$/.test(raw)) {
    return '0';
  }

  const normalized = scaledIntToNormal(raw, decimalPlaces, {
    fixed: false,
    trimZeros: true,
  });

  return parseDecimalInput(normalized) ? normalized : '0';
}

function getOperationUsdValue(operation) {
  return normalizeScaledAmount(
    operation?.value_usd
    ?? operation?.profit_outcome_value_usd
    ?? operation?.profit_income_value_usd
    ?? operation?.amount_usd
    ?? operation?.amountUsd,
    USD_AMOUNT_DECIMALS
  );
}

function formatMoneyAmount(value, isIncome) {
  const decimal = parseDecimalInput(value) || parseDecimalInput('0');
  const absolute = decimal.abs();
  const sign = isIncome ? '+' : '-';

  return `${sign} ${formatDecimal(absolute, 2, {
    prefix: '$',
    minFractionDigits: 2,
  })}`;
}

function formatCurrencyAmount(amount, currency) {
  const normalizedCurrency = String(currency || '').trim().toUpperCase();
  const decimal = parseDecimalInput(amount);

  if (!decimal || !normalizedCurrency) {
    return '';
  }

  return `${formatDecimal(decimal, 2, {
    trimZeros: true,
    minFractionDigits: 0,
  })} ${normalizedCurrency}`;
}

function getCryptoData(operation) {
  return operation?.crypto_data || operation?.cryptoData || null;
}

function getCryptoCurrency(operation) {
  const currency = getCryptoData(operation)?.currency;

  return currency?.data || currency || null;
}

function getCurrencyDecimalPlaces(currency) {
  const decimalPlaces = Number.parseInt(currency?.decimal_places ?? currency?.decimalPlaces, 10);

  return Number.isFinite(decimalPlaces) && decimalPlaces >= 0 ? decimalPlaces : 0;
}

function getTxHash(operation) {
  const cryptoData = getCryptoData(operation);

  return (
    cryptoData?.tx_id
    || cryptoData?.tx_hash
    || operation?.tx_hash
    || operation?.txHash
    || ''
  );
}

function getExplorerUrl(operation) {
  const cryptoData = getCryptoData(operation);
  const currency = getCryptoCurrency(operation);
  const network = cryptoData?.network || cryptoData?.currency?.network;
  const txHash = getTxHash(operation);
  const networks = currency?.networks?.total;
  const normalizedNetwork = String(network || '').replace(/-/g, '');
  const explorer = networks?.[network]?.explorer
    || networks?.[normalizedNetwork]?.explorer;

  if (cryptoData?.url) {
    return cryptoData.url;
  }

  if (!explorer || !txHash) {
    return '';
  }

  return explorer.replace('{tx_id}', txHash).replace('{tx_hash}', txHash);
}

function getExplorerName(explorerUrl) {
  if (!explorerUrl) {
    return '';
  }

  try {
    const host = new URL(explorerUrl).hostname.replace(/^www\./, '');

    if (host.includes('etherscan')) return 'Etherscan';
    if (host.includes('tronscan')) return 'Tronscan';

    return host;
  } catch {
    return '';
  }
}

function getOperationCard(operation) {
  return operation?.card || operation?.card_data || operation?.cardData || null;
}

function getCardLast4(operation) {
  const card = getOperationCard(operation);

  return String(card?.last4 || card?.lastFour || card?.last_digits || '')
    .trim()
    .slice(-4);
}

function getCardName(operation) {
  const card = getOperationCard(operation);

  return String(
    card?.static_data?.name
    || card?.staticData?.name
    || card?.name
    || ''
  ).trim();
}

function getCardLabel(operation) {
  const cardName = getCardName(operation);
  const last4 = getCardLast4(operation);

  if (!last4) {
    return '';
  }

  return `${cardName ? `${cardName} ` : ''}*${last4}`;
}

function getCardStyle(operation) {
  const card = getOperationCard(operation);

  return String(
    card?.static_data?.style
    || card?.staticData?.style
    || card?.style
    || ''
  ).trim();
}

function getCryptoAmountValue(operation) {
  const cryptoData = getCryptoData(operation);
  const currency = getCryptoCurrency(operation);
  const directAmount = cryptoData?.amount
    ?? cryptoData?.value
    ?? cryptoData?.currency_amount
    ?? cryptoData?.currencyAmount
    ?? cryptoData?.received_amount
    ?? cryptoData?.receivedAmount;

  if (directAmount !== undefined && directAmount !== null && directAmount !== '') {
    return normalizeScaledAmount(directAmount, getCurrencyDecimalPlaces(currency));
  }

  return '';
}

function formatCryptoAmount(value, asset, decimalPlaces) {
  const decimal = parseDecimalInput(value);

  if (!decimal || !asset) {
    return '';
  }

  return `${formatDecimal(decimal.abs(), Math.min(Math.max(decimalPlaces, 2), 8), {
    trimZeros: true,
    minFractionDigits: 0,
  })} ${asset}`;
}

function getTransactionTitle(operation) {
  const merchant = getEnrichedMerchant(operation);
  const type = String(operation?.type || '').toLowerCase();
  const fallbackTitle = (
    merchant?.name
    || merchant?.description
    || operation?.description
    || ''
  );

  if (isCardCreateOperation(operation)) {
    return LOCALIZED_TRANSACTION_TEXT.cardIssuancePayment;
  }

  if (isPaymentWithdrawalOperation(operation)) {
    return LOCALIZED_TRANSACTION_TEXT.cashWithdrawal;
  }

  if (isPaymentCanceledOperation(operation) && !fallbackTitle) {
    return LOCALIZED_TRANSACTION_TEXT.paymentCanceled;
  }

  if (type.includes('deposit') && !merchant?.name && !operation?.description) {
    return LOCALIZED_TRANSACTION_TEXT.depositAccount;
  }

  if (type.includes('refund')) {
    return fallbackTitle
      ? `${LOCALIZED_TRANSACTION_TEXT.refund} · ${fallbackTitle}`
      : LOCALIZED_TRANSACTION_TEXT.refund;
  }

  return (
    fallbackTitle
    || operation?.type
    || 'Transaction'
  );
}

function getTransactionDescription(operation) {
  const merchant = getEnrichedMerchant(operation);

  return merchant?.description || operation?.description || '';
}

function normalizeOperation(operation = {}) {
  const id = getOperationId(operation);
  const merchant = getEnrichedMerchant(operation);
  const title = getTransactionTitle(operation);
  const description = getTransactionDescription(operation);
  const income = isIncomeOperation(operation);
  const isCardCreate = isCardCreateOperation(operation);
  const isPaymentCanceled = isPaymentCanceledOperation(operation);
  const isPaymentWithdrawal = isPaymentWithdrawalOperation(operation);
  const operationType = String(operation?.type || '').toLowerCase();
  const isRefund = operationType.includes('refund');
  const usdValue = getOperationUsdValue(operation);
  const amountDecimal = parseDecimalInput(usdValue) || parseDecimalInput('0');
  const signedAmount = income ? amountDecimal : amountDecimal.neg();
  const categoryKey = getCategoryKey(operation);
  const txHash = getTxHash(operation);
  const cryptoData = getCryptoData(operation);
  const cryptoCurrency = getCryptoCurrency(operation);
  const network = cryptoData?.network || cryptoData?.currency?.network || operation?.network || '';
  const cryptoAsset = String(cryptoCurrency?.code || '').trim().toUpperCase();
  const cryptoDecimalPlaces = getCurrencyDecimalPlaces(cryptoCurrency);
  const cryptoAmount = getCryptoAmountValue(operation);
  const cryptoAmountFormatted = formatCryptoAmount(
    cryptoAmount,
    cryptoAsset,
    cryptoDecimalPlaces
  );
  const usdAmountFormatted = formatDecimal(amountDecimal.abs(), 2, {
    prefix: '$',
    minFractionDigits: 2,
  });
  const explorerUrl = getExplorerUrl(operation);
  const iconLetterSource = merchant?.name
    || description
    || (isRefund ? 'Refund' : isPaymentCanceled ? 'Payment' : title);
  const originalCurrencyRaw = operation?.original_currency || operation?.originalCurrency || '';
  const originalCurrency =
    typeof originalCurrencyRaw === 'object'
      ? originalCurrencyRaw?.code || ''
      : originalCurrencyRaw;
  const originalCurrencyAmount = operation?.original_currency_amount
    ?? operation?.originalCurrencyAmount
    ?? '';
  const originalAmountValue = String(originalCurrencyAmount || '').trim().replace(',', '.');
  const originalAmountFormatted = formatCurrencyAmount(originalAmountValue, originalCurrency);

  return {
    raw: operation,
    id,
    type: income ? TRANSACTION_TYPE_FILTERS.INCOME : TRANSACTION_TYPE_FILTERS.EXPENSE,
    operationType: operation?.type || '',
    icon: getInitialLetter(iconLetterSource),
    iconLetter: getInitialLetter(iconLetterSource),
    iconUrl: isRefund
      ? buildBrandfetchIconUrl(merchant?.domain)
      : income
        ? getCurrencyIconUrl(cryptoCurrency)
        : isCardCreate || isPaymentWithdrawal
          ? ''
          : buildBrandfetchIconUrl(merchant?.domain),
    iconBackground: merchant?.initial_background_colour || '',
    iconVariant: isPaymentCanceled
      ? 'payment-canceled'
      : isRefund
        ? 'refund'
        : isCardCreate
          ? 'card-issue'
          : isPaymentWithdrawal
            ? 'cash-withdrawal'
            : income
              ? 'deposit'
              : 'brand',
    isInternal: income || isCardCreate,
    merchant: title,
    description,
    originalDescription: typeof operation?.description === 'string'
      ? operation.description.trim()
      : '',
    subtitle: network || '',
    createdAt: getOperationDateValue(operation),
    amount: Number(signedAmount.toString()),
    amountValue: signedAmount.toString(),
    amountFormatted: formatMoneyAmount(usdValue, income),
    originalCurrency,
    originalCurrencyAmount: originalAmountValue,
    originalAmountFormatted,
    cryptoAsset,
    cryptoAmount,
    cryptoAmountFormatted,
    exchangeFormatted: cryptoAmountFormatted
      ? `${cryptoAmountFormatted} → ${usdAmountFormatted}`
      : '',
    network,
    amountVariant: income ? 'positive' : 'negative',
    cardId: operation?.card_id
      || operation?.cardId
      || getOperationCard(operation)?._id
      || getOperationCard(operation)?.id
      || 'all',
    cardLabel: getCardLabel(operation),
    cardName: getCardName(operation),
    cardLast4: getCardLast4(operation),
    cardStyle: getCardStyle(operation),
    category: categoryKey,
    reference: operation?.reference || operation?.external_id || '',
    txHash,
    blockscannerUrl: explorerUrl,
    explorerName: getExplorerName(explorerUrl),
    status: operation?.status || '',
  };
}

function normalizeOperationsResponse(response) {
  return getResponseData(response).map(normalizeOperation).filter((transaction) => transaction.id);
}

export async function fetchTransactionsPage(options = {}) {
  const {
    limit = 25,
    lastId,
    filters,
    requireUnlockSession = true,
  } = options;

  if (requireUnlockSession && !isUnlockSessionReady()) {
    return {
      items: [],
      isMore: false,
      lastId: lastId || null,
    };
  }

  const response = await operationsApi.getAll({
    limit,
    lastId,
    filters,
  });
  const responseItems = getResponseData(response);

  return {
    items: normalizeOperationsResponse(response),
    isMore: Boolean(response?.is_more),
    lastId: getOperationId(responseItems.at(-1)) || lastId || null,
  };
}

function upsertTransactions(items, append = false) {
  const existing = append ? transactionsState.items : [];
  const byId = new Map(existing.map((item) => [item.id, item]));

  items.forEach((item) => {
    byId.set(item.id, item);
  });

  const nextItems = Array.from(byId.values());

  transactionsState = {
    ...transactionsState,
    items: sortTransactions(nextItems, DEFAULT_HISTORY_FILTERS.sortBy),
    lastId: nextItems.at(-1)?.id || transactionsState.lastId,
  };
}

export function subscribeTransactions(onStoreChange) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  window.addEventListener(TRANSACTIONS_UPDATED_EVENT, onStoreChange);

  return () => window.removeEventListener(TRANSACTIONS_UPDATED_EVENT, onStoreChange);
}

export function getTransactionsSnapshot() {
  return transactionsState;
}

export async function loadTransactions(options = {}) {
  const {
    limit = 25,
    append = false,
    silent = false,
    filters,
    requireUnlockSession = true,
  } = options;

  if (requireUnlockSession && !isUnlockSessionReady()) {
    return transactionsState.items;
  }

  if (!silent) {
    transactionsState = {
      ...transactionsState,
      isLoading: true,
      error: null,
    };
    emitTransactionsUpdate();
  }

  try {
    const response = await operationsApi.getAll({
      limit,
      lastId: append ? transactionsState.lastId : undefined,
      filters,
    });
    const items = normalizeOperationsResponse(response);

    upsertTransactions(items, append);
    transactionsState = {
      ...transactionsState,
      isLoaded: true,
      isLoading: false,
      isMore: Boolean(response?.is_more),
      error: null,
    };
    emitTransactionsUpdate();

    return transactionsState.items;
  } catch (error) {
    transactionsState = {
      ...transactionsState,
      isLoaded: true,
      isLoading: false,
      error,
    };
    emitTransactionsUpdate();
    throw error;
  }
}

export async function loadTransactionById(id) {
  const existing = getTransactionById(id);

  if (existing) {
    return existing;
  }

  transactionsState = {
    ...transactionsState,
    isLoading: true,
    error: null,
  };
  emitTransactionsUpdate();

  try {
    const response = await operationsApi.getById({ id });
    const transaction = normalizeOperation(response);

    if (transaction.id) {
      upsertTransactions([transaction], true);
    }
    transactionsState = {
      ...transactionsState,
      isLoaded: true,
      isLoading: false,
      error: null,
    };
    emitTransactionsUpdate();

    return transaction;
  } catch (error) {
    transactionsState = {
      ...transactionsState,
      isLoaded: true,
      isLoading: false,
      error,
    };
    emitTransactionsUpdate();
    throw error;
  }
}

function getTransactionDateValue(transaction) {
  return transaction.createdAt
    || transaction.created_at
    || transaction.utc_datetime
    || transaction.date
    || null;
}

function parseTransactionDateValue(dateValue) {
  const raw = String(dateValue || '').trim();

  if (!raw) {
    return null;
  }

  const parsedDate = /(?:z|[+-]\d{2}:?\d{2})$/i.test(raw)
    ? dayjs.utc(raw).local()
    : dayjs(raw);

  return parsedDate.isValid() ? parsedDate : null;
}

function getTransactionDay(transaction) {
  const dateValue = getTransactionDateValue(transaction);

  return parseTransactionDateValue(dateValue);
}

function getComputedDateGroup(transaction) {
  const explicitGroup = transaction.dateGroup || transaction.date_group;

  if (explicitGroup === 'today' || explicitGroup === 'yesterday') {
    return explicitGroup;
  }

  const date = getTransactionDay(transaction);

  if (!date) {
    return explicitGroup || 'older';
  }

  if (date.isSame(dayjs(), 'day')) {
    return 'today';
  }

  if (date.isSame(dayjs().subtract(1, 'day'), 'day')) {
    return 'yesterday';
  }

  return date.format('YYYY-MM-DD');
}

function getTransactionDateLabel(groupKey, firstTransaction, language, history) {
  const locale = getLocale(language);

  if (groupKey === 'today') {
    return history?.today || 'Today';
  }

  if (groupKey === 'yesterday') {
    return history?.yesterday || 'Yesterday';
  }

  const date = dayjs(groupKey);

  if (date.isValid()) {
    return date.locale(locale).format('D MMM YYYY');
  }

  const fallbackDate = getTransactionDay(firstTransaction);
  return fallbackDate
    ? fallbackDate.locale(locale).format('D MMM YYYY')
    : groupKey;
}

function compareTransactionGroups(left, right) {
  const leftOrder = KNOWN_DATE_GROUP_ORDER[left.key];
  const rightOrder = KNOWN_DATE_GROUP_ORDER[right.key];

  if (leftOrder !== undefined || rightOrder !== undefined) {
    return (leftOrder ?? 100) - (rightOrder ?? 100);
  }

  const leftDate = dayjs(left.key);
  const rightDate = dayjs(right.key);

  if (leftDate.isValid() && rightDate.isValid()) {
    return rightDate.valueOf() - leftDate.valueOf();
  }

  return DATE_RANGE_ORDER.indexOf(left.key) - DATE_RANGE_ORDER.indexOf(right.key);
}

function matchesType(transaction, type) {
  if (type === TRANSACTION_TYPE_FILTERS.ALL) {
    return true;
  }
  return transaction.type === type;
}

function matchesQuery(transaction, query) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  return [
    transaction.merchant,
    transaction.description,
    transaction.category,
    transaction.reference,
    transaction.txHash,
    transaction.tx_hash,
  ].some((value) => String(value || '').toLowerCase().includes(normalized));
}

function matchesCard(transaction, cardId) {
  if (!cardId || cardId === 'all') {
    return true;
  }
  return transaction.cardId === cardId;
}

function matchesStatus(transaction, status) {
  if (!status || status === 'all') {
    return true;
  }
  return transaction.status === status;
}

function sortTransactions(transactions, sortBy) {
  const sorted = [...transactions];

  if (sortBy === 'oldest') {
    return sorted.sort((left, right) => {
      const leftDate = getTransactionDay(left)?.valueOf() || 0;
      const rightDate = getTransactionDay(right)?.valueOf() || 0;
      return leftDate - rightDate;
    });
  }

  if (sortBy === 'highest') {
    return sorted.sort((left, right) => Math.abs(right.amount) - Math.abs(left.amount));
  }

  return sorted.sort((left, right) => {
    const leftDate = getTransactionDay(left)?.valueOf() || 0;
    const rightDate = getTransactionDay(right)?.valueOf() || 0;
    return rightDate - leftDate;
  });
}

export function getTransactionById(id) {
  return transactionsState.items.find((transaction) => transaction.id === id) ?? null;
}

export function getTransactionRouteSnapshot(transaction) {
  if (!transaction || typeof transaction !== 'object') {
    return null;
  }

  const snapshot = { ...transaction };

  delete snapshot.raw;

  return snapshot;
}

export function filterTransactions(filters = DEFAULT_HISTORY_FILTERS, source = transactionsState.items) {
  const merged = { ...DEFAULT_HISTORY_FILTERS, ...filters };

  const filtered = source.filter(
    (transaction) =>
      matchesType(transaction, merged.type) &&
      matchesQuery(transaction, merged.query) &&
      matchesCard(transaction, merged.cardId) &&
      matchesStatus(transaction, merged.status)
  );

  return sortTransactions(filtered, merged.sortBy);
}

export function groupTransactionsByDate(transactions, language, history) {
  const groups = new Map();

  transactions.forEach((transaction) => {
    const key = getComputedDateGroup(transaction);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(transaction);
  });

  return Array.from(groups.entries()).map(([key, items]) => ({
    key,
    label: getTransactionDateLabel(key, items[0], language, history),
    items,
  })).sort(compareTransactionGroups);
}

export function getSearchSummary(transactions) {
  const total = transactions.reduce((sum, transaction) => sum + Math.abs(transaction.amount), 0);
  const formatted = total.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });

  return {
    count: transactions.length,
    total: formatted,
  };
}

export function formatTransactionListDate(transaction, language, history) {
  const date = getTransactionDay(transaction);

  if (!date) {
    return transaction.date || '';
  }

  const locale = getLocale(language);
  const explicitGroup = transaction.dateGroup || transaction.date_group;
  const time = date.locale(locale).format('HH:mm');

  if (explicitGroup === 'today' || (!explicitGroup && date.isSame(dayjs(), 'day'))) {
    return `${history?.today || 'Today'}, ${time}`;
  }

  if (
    explicitGroup === 'yesterday'
    || (!explicitGroup && date.isSame(dayjs().subtract(1, 'day'), 'day'))
  ) {
    return `${history?.yesterday || 'Yesterday'}, ${time}`;
  }

  return date.locale(locale).format('D MMM YYYY, HH:mm');
}

export function formatTransactionListTime(transaction, language) {
  const date = getTransactionDay(transaction);

  if (!date) {
    return transaction.time || '';
  }

  return date.locale(getLocale(language)).format('HH:mm');
}

export function formatTransactionFullDate(transaction, language) {
  const date = getTransactionDay(transaction);

  if (!date) {
    return transaction.date || '';
  }

  return date.locale(getLocale(language)).format('D MMM YYYY, HH:mm');
}

export function getLocalizedTransactionText(value, history) {
  const text = String(value || '');
  const depositPrefix = `${LOCALIZED_TRANSACTION_TEXT.depositAccount} `;
  const refundPrefix = `${LOCALIZED_TRANSACTION_TEXT.refund} · `;

  if (text.startsWith(depositPrefix)) {
    return history?.depositAccount || text;
  }

  if (text.startsWith(refundPrefix)) {
    const refund = history?.refund || LOCALIZED_TRANSACTION_TEXT.refund;

    return `${refund} · ${text.slice(refundPrefix.length)}`;
  }

  if (
    text === LOCALIZED_TRANSACTION_TEXT.cardIssuancePayment
    || CARD_CREATE_OPERATION_TYPES.has(text.toLowerCase())
  ) {
    return history?.cardIssuancePayment || text;
  }

  if (
    text === LOCALIZED_TRANSACTION_TEXT.cashWithdrawal
    || PAYMENT_WITHDRAWAL_OPERATION_TYPES.has(text.toLowerCase())
  ) {
    return history?.cashWithdrawal || text;
  }

  if (text === LOCALIZED_TRANSACTION_TEXT.depositAccount) {
    return history?.depositAccount || text;
  }

  if (text === LOCALIZED_TRANSACTION_TEXT.paymentCanceled) {
    return history?.paymentCanceled || text;
  }

  if (text === LOCALIZED_TRANSACTION_TEXT.refund) {
    return history?.refund || text;
  }

  if (text === 'Top up USDT') {
    return history?.topUpUsdt || text;
  }

  return value;
}

export function getLocalizedTransactionCategory(transaction, history) {
  const key = typeof transaction === 'string' ? transaction : transaction?.category;

  return history?.operationCategories?.[key] || key || history?.operationCategories?.general || 'General';
}
